import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core';
import { accessBounce } from '@core/access-bounce';
import { PlatformService } from './core/services/platform.service';
import { FormsModule } from '@angular/forms';
import {
  BrowserModule,
  provideClientHydration,
  withEventReplay,
  withNoHttpTransferCache,
} from '@angular/platform-browser';
import { provideRouter, RouteReuseStrategy, withInMemoryScrolling } from '@angular/router';
import { ServiceAwareRouteReuseStrategy } from './core/strategies/route-reuse.strategy';
import { provideServiceWorker } from '@angular/service-worker';
import { provideIcons } from '@ng-icons/core';
import { provideHlmSidebarConfig } from '@spartan-ng/helm/sidebar';
import {
  lucideUserMinus,
  lucideMoreVertical,
  lucideEye,
  lucideEyeOff,
  lucideExternalLink,
  lucideRefreshCw,
  lucideSmartphone,
  lucideCloud,
  lucideMonitor,
  lucideTerminal,
  lucideRocket,
  lucideCheckCircle,
  lucideCheckCircle2,
  lucideUserPlus,
  lucideEuro,
  lucideArrowRight,
  lucideCode,
  lucideShield,
  lucideActivity,
  lucideZap,
  lucideFolder,
  lucidePlug,
  lucideGlobe,
  lucideRadar,
  lucideSearch,
  lucideFileText,
  lucideBarChart,
  lucideQuote,
  lucideCopy,
  lucideList,
  lucideGrid3x3,
  lucidePin,
  lucideCalendar,
  lucideUser,
  lucideServer,
  lucideArrowLeft,
  lucideUserCircle,
  lucideMenu,
  lucideX,
  lucideClock,
  lucideCloudOff,
  lucideLockOpen,
  lucideTrendingUp,
  lucideHeadphones,
  lucideLifeBuoy,
  lucideHelpCircle,
  lucideHouse,
  lucideDatabase,
  lucideSettings,
  lucidePalette,
  lucideWrench,
  lucidePartyPopper,
  lucideBrain,
  lucideAlertCircle,
  lucideXCircle,
  lucideGaugeCircle,
  lucideLayoutGrid,
  lucidePlus,
  lucideRuler,
  lucideMessageCircle,
  lucideArchive,
  lucideShieldCheck,
  lucideFlaskConical,
  lucideBriefcase,
  lucideHeart,
  lucideMail,
  lucideChevronUp,
  lucideChevronDown,
  lucideChevronsDownUp,
  lucideSparkles,
  lucideScissors,
  lucideBot,
  lucideBadge,
  lucideGraduationCap,
  lucideLock,
  lucideUsers,
  lucideLanguages,
  lucideUpload,
  lucideShieldOff,
  lucideScale,
  lucideKeyRound,
  lucideMessageSquare,
  lucidePuzzle,
  lucideWebhook,
  lucideBellRing,
  lucideLogOut,
  lucideTriangleAlert,
  lucideBuilding,
  lucideCheck,
  lucideInfo,
  lucideCreditCard,
  lucidePencil,
  lucideChevronsUpDown,
  lucideLineChart,
  lucideTable,
  lucideGauge,
  lucideLoader2,
  lucidePlay,
  lucidePause,
  lucideChevronLeft,
  lucideChevronRight,
  lucideTrash2,
  lucideDownload,
  lucideBookOpen,
  lucideToggleRight,
  lucideToggleLeft,
  lucideFilter,
  lucideFileJson,
  lucideCode2,
  lucideLink,
  lucideBarChart3,
  lucideRadio,
  lucideListFilter
} from '@ng-icons/lucide';
import { CoreModule } from '@core/core.module';
import { environment } from 'src/environments/environment';
import { routes } from './app.routes';
import { SharedModule } from './shared/shared.module';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    // GET /platform before the first screen: service URLs and status nodes come from the admin
    provideAppInitializer(() => accessBounce()),
    provideAppInitializer(() => inject(PlatformService).load()),
    { provide: RouteReuseStrategy, useClass: ServiceAwareRouteReuseStrategy },
    importProvidersFrom(BrowserModule, FormsModule, SharedModule, CoreModule),
    provideIcons({
      lucideEye,
      lucideEyeOff,
      lucideExternalLink,
      lucideRefreshCw,
      lucideSmartphone,
      lucideCloud,
      lucideMonitor,
      lucideTerminal,
      lucideRocket,
      lucideCheckCircle,
      lucideCheckCircle2,
      lucideInfo,
      lucideUserPlus,
      lucideEuro,
      lucideArrowRight,
      lucideCode,
      lucideShield,
      lucideActivity,
      lucideZap,
      lucideFolder,
      lucidePlug,
      lucideGlobe,
      lucideRadar,
      lucideSearch,
      lucideFileText,
      lucideBarChart,
      lucideQuote,
      lucideCopy,
      lucideList,
      lucideGrid3x3,
      lucidePin,
      lucideCalendar,
      lucideUser,
      lucideServer,
      lucideArrowLeft,
      lucideUserCircle,
      lucideMenu,
      lucideX,
      lucideClock,
      lucideCloudOff,
      lucideLockOpen,
      lucideTrendingUp,
      lucideHeadphones,
      lucideLifeBuoy,
      lucideHelpCircle,
      lucideHouse,
      lucideDatabase,
      lucideSettings,
      lucidePalette,
      lucideWrench,
      lucidePartyPopper,
      lucideBrain,
      lucideAlertCircle,
      lucideXCircle,
      lucideGaugeCircle,
      lucideLayoutGrid,
      lucidePlus,
      lucideRuler,
      lucideMessageCircle,
      lucideArchive,
      lucideShieldCheck,
      lucideFlaskConical,
      lucideBriefcase,
      lucideHeart,
      lucideMail,
      lucideChevronUp,
      lucideChevronDown,
      lucideChevronsDownUp,
      lucideChevronsUpDown,
      lucideSparkles,
      lucideScissors,
      lucideBot,
      lucideBadge,
      lucideGraduationCap,
      lucideLock,
      lucideUsers,
      lucideLanguages,
      lucideUpload,
      lucideShieldOff,
      lucideScale,
      lucideKeyRound,
      lucideMessageSquare,
      lucidePuzzle,
      lucideWebhook,
      lucideBellRing,
      lucideLogOut,
      lucideTriangleAlert,
      lucideBuilding,
      lucideCreditCard,
      lucideCheck,
      lucideUserMinus,
      lucideMoreVertical,
      lucidePencil,
      lucideLineChart,
      lucideTable,
      lucideGauge,
      lucideLoader2,
      lucidePlay,
      lucidePause,
      lucideChevronLeft,
      lucideChevronRight,
      lucideTrash2,
      lucideDownload,
      lucideBookOpen,
      lucideToggleRight,
      lucideToggleLeft,
      lucideFilter,
      lucideFileJson,
      lucideCode2,
      lucideLink,
      lucideBarChart3,
      lucideRadio,
      lucideListFilter
    }),
    provideHlmSidebarConfig({
      sidebarWidth: '10rem',
      sidebarWidthIcon: '2.5rem',
      sidebarCookieName: 'sidebar:state',
      sidebarCookieMaxAge: 60 * 60 * 24 * 7, // 7 days
      sidebarKeyboardShortcut: 'b',
      mobileBreakpoint: '768px',
    }),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      })
    ),
    // provideAnimations(), uncomment to enable animations
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.UPDATE_CHECK_INTERVAL > 0,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideClientHydration(withEventReplay(), withNoHttpTransferCache()),
  ],
};
